// React
import { FunctionComponent, useState } from "react"

// UI Library
import { Button, Modal, Form, Spinner } from "react-bootstrap"

// Interfaces
import { I_IncidentAction, I_SafetyEvent, I_SafetyEventWithApprovalHistory } from "../../interfaces"

// Services
import VIS from "../../VehicleIncidentsService"
import approvalService from "src/components/approvals/ApprovalService"
import ActionApproval from "src/components/approvals/crud/ActionApproval"
import CreateAndActionWorkflow from "src/components/approvals/crud/CreateAndActionWorkflow"
import snackBar from "src/utilities/snackBar/SnackBar"

// Constants
import { SAFETY_EVENT_RECORD_TYPE_ID, getActionTypeId, getPendingStatusTypeId, getTemplateGroupId } from "../../constants/VehicleIncidentsApprovalConfig"

//Local Components
import VehicleIncidentsMiniInfo from "../VehicleIncidentsMiniInfo/VehicleIncidentsMiniInfo"

// Redux
import { AppState } from "src/store"
import { connect } from "react-redux"

// Styles
import "./VehicleIncidentsStartDiscipline.css"

interface Props {
  userId: number
  incidentAction: I_IncidentAction
}

// Modal for starting a disciplinary form workflow for a safety event
const VehicleIncidentsStartDiscipline: FunctionComponent<Props> = (props) => {
  const { userId, incidentAction } = props

  // Destructure helpers from VIS
  const { setIncidentAction, retrieveEventFromMap, refresh, findStepId } = VIS

  // Resolve IDs from reference
  const disciplineActionTypeId = getActionTypeId(approvalService.reference, "Discipline")
  const pendingStatusTypeId = getPendingStatusTypeId(approvalService.reference)
  const templateGroupId = getTemplateGroupId(approvalService.reference)

  // Local state
  const [comments, setComments] = useState("")
  const [busy, setBusy] = useState(false)

  // Retrieve event details from cache
  const event: I_SafetyEvent | I_SafetyEventWithApprovalHistory | null =
    retrieveEventFromMap(incidentAction.id || -1)

  // If no event found, render nothing
  if (!event) return null

  const handleStartDiscipline = async () => {
    setBusy(true)

    const isNew = !VIS.isWithApprovalHistory(event)
    const { ObjectID, EmployeeID } = event
    let success = false

    if (isNew) {
      // Create and immediately act on a new approval workflow
      const payload = {
        RecordTypeId: SAFETY_EVENT_RECORD_TYPE_ID,
        RecordId: ObjectID,
        EmployeeId: EmployeeID,
        InitiatedBy: -1,
        StatusTypeId: pendingStatusTypeId,
        ApproverUserIds: [userId],
        TemplateGroupId: templateGroupId
      }

      success = await CreateAndActionWorkflow(payload, userId, disciplineActionTypeId, comments)
    } else {
      // Action an existing approval step
      success = await ActionApproval({
        ApprovalStepId: findStepId(event),
        ActionedBy: userId,
        ApprovalActionTypeId: disciplineActionTypeId,
        Comments: comments
      })
    }

    await refresh(VIS.currentView)

    if (success) {
      snackBar({ type: "primary", text: "Discipline started", timeout: 4000 })
    } else {
      snackBar({ type: "error", text: "Failed to start discipline", timeout: 4000 })
    }

    setBusy(false)
  }

  return (
    <div className="VehicleIncidentsStartDiscipline">
      <Modal
        id="vehicle-incidents-start-discipline-modal"
        show
        backdrop="static"
        keyboard
        centered
        className="VehicleIncidentsWorkflowModal"
        animation={false}
      >
        <Modal.Header>
          <div className="VehicleIncidentsStartDisciplineHeader VehicleIncidentsWorkflowModalHeader">
            Create Discipline
            {busy && <Spinner
              className="VehicleIncidentsWorkflowModalHeaderSpinner"
              animation="border"
            />}
          </div>
        </Modal.Header>

        <Modal.Body>
          <div className="VehicleIncidentsStartDisciplineModalBody">
            <VehicleIncidentsMiniInfo
              id={incidentAction.id}
            />
            <Form.Group controlId="disciplineComments">
              <Form.Label>Comments</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
              />
            </Form.Group>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <div className="VehicleIncidentsStartDisciplineModalFooter d-flex justify-content-between w-100">
            {/* Back to action chooser */}
            <Button
              variant="secondary"
              onClick={() =>
                setIncidentAction({
                  action: 1,
                  id: incidentAction.id
                })
              }
              disabled={busy}
            >
              Back
            </Button>

            {/* Submit discipline action */}
            <Button
              variant="primary"
              onClick={handleStartDiscipline}
              disabled={busy}
            >
              {busy ? "Sending..." : "Send to Tech"}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

// Map Redux state to props
const mapStateToProps = (state: AppState) => ({
  userId: parseInt(state.user.userId)
})

export default connect(mapStateToProps)(VehicleIncidentsStartDiscipline)